/**
 * wander — pure, Phaser-free overworld movement. The owner found the old
 * "lerp continuously toward a random far point" wander too *floaty*: characters
 * glided diagonally at varying speeds toward distant targets, reading like drifting
 * rather than walking. This module replaces it with DELIBERATE classic-overworld
 * walking, kept pure so it is unit-testable in the node env (the scenes themselves
 * import Phaser and aren't unit-tested live):
 *
 *   pick a CARDINAL direction (up/down/left/right — never diagonal) → walk a short,
 *   whole number of TILES at a steady, calm speed → STOP and idle for a beat →
 *   (sometimes) turn → walk again.
 *
 * The state machine has two phases: 'walking' (advancing toward a tile-aligned goal
 * in one cardinal axis) and 'pausing' (standing still, facing the last direction).
 * Speed is constant; distance is quantized to tiles; direction is one of four — so
 * the result reads like a Pokemon town resident, not a floating blob.
 *
 * The scene drives this each frame with `stepWander(state, dt, bounds, rng)` and
 * applies the returned position/facing to its sprites. The follower trail and the
 * deterministic NPC-lead-species pick live here too, so both populated scenes share
 * one tested brain.
 */

/** The four cardinal facings used for deliberate walking (no diagonal gliding). */
export type Cardinal = 'north' | 'south' | 'east' | 'west';

export const CARDINALS: readonly Cardinal[] = ['south', 'north', 'east', 'west'];

/** Unit vector for a cardinal facing (screen space: +y is DOWN = south). */
export function cardinalVector(dir: Cardinal): { dx: number; dy: number } {
  switch (dir) {
    case 'north':
      return { dx: 0, dy: -1 };
    case 'south':
      return { dx: 0, dy: 1 };
    case 'east':
      return { dx: 1, dy: 0 };
    case 'west':
      return { dx: -1, dy: 0 };
  }
}

/**
 * The PMD 8-way direction string a cardinal maps to (the four diagonal rows are simply
 * never used). Kept here (a string-literal map, no Phaser) so the scenes can drive the
 * directional PMD chibi frames straight from a Cardinal.
 */
export function cardinalToDirection(dir: Cardinal): 'north' | 'south' | 'east' | 'west' {
  return dir;
}

/** Map a (possibly diagonal) movement vector to the dominant cardinal, or null. */
export function cardinalFromVector(dx: number, dy: number, deadzone = 0.001): Cardinal | null {
  if (dx * dx + dy * dy < deadzone * deadzone) return null;
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? 'east' : 'west';
  return dy >= 0 ? 'south' : 'north';
}

/** A rectangular walkable region (sprite *feet* are clamped a margin inside it). */
export interface WanderBounds {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  /** Keep feet at least this far from each edge. */
  readonly margin: number;
}

/** Tunables shared by residents + the player follower (calm, classic-overworld). */
export interface WanderConfig {
  /** Pixels per tile — distance is quantized to whole tiles so steps feel discrete. */
  readonly tile: number;
  /** Steady walk speed (px/sec). Constant — no easing, no acceleration. */
  readonly speed: number;
  /** Min/max whole tiles to walk in one go before stopping. */
  readonly minTiles: number;
  readonly maxTiles: number;
  /** Min/max seconds to idle between walks. */
  readonly minPause: number;
  readonly maxPause: number;
  /** Chance [0..1] of changing direction when starting a new walk (else continue). */
  readonly turnChance: number;
}

export const DEFAULT_WANDER: WanderConfig = {
  tile: 24,
  speed: 46,
  minTiles: 1,
  maxTiles: 3,
  minPause: 0.7,
  maxPause: 2.6,
  turnChance: 0.7,
};

/** A minimal RNG surface (so the same shape works for Math.random or a seeded Rng). */
export interface RandomSource {
  /** A float in [0, 1). */
  float(): number;
}

/** Math.random adapter (the town crowd's ambient wander needn't be deterministic). */
export const MATH_RANDOM: RandomSource = { float: () => Math.random() };

/** The live state of one wandering actor's deliberate movement. */
export interface WanderState {
  x: number;
  y: number;
  facing: Cardinal;
  /** 'walking' toward `goal`, or 'pausing' (standing idle). */
  phase: 'walking' | 'pausing';
  /** Seconds remaining in the current pause (phase === 'pausing'). */
  pauseLeft: number;
  /** The tile-aligned destination of the current walk (phase === 'walking'). */
  goalX: number;
  goalY: number;
}

/** What `stepWander` reports back so the scene can drive its sprite's anim. */
export interface WanderStep {
  /** True if the actor moved this frame (drive the walk animation/bob). */
  moving: boolean;
  /** True the instant a new walk begins (so callers can (re)start the walk anim). */
  startedWalk: boolean;
  /** True the instant the actor stops (so callers can settle to idle). */
  stopped: boolean;
  /** True if the facing changed this frame. */
  turned: boolean;
}

/** Seed a wander state at (x, y), starting in a short randomized idle (varied starts). */
export function initWander(
  x: number,
  y: number,
  rng: RandomSource = MATH_RANDOM,
  cfg: WanderConfig = DEFAULT_WANDER,
): WanderState {
  return {
    x,
    y,
    facing: CARDINALS[Math.floor(rng.float() * CARDINALS.length)] ?? 'south',
    phase: 'pausing',
    // Stagger initial idles so a crowd never sets off in lockstep.
    pauseLeft: rng.float() * cfg.maxPause,
    goalX: x,
    goalY: y,
  };
}

/**
 * Pick the next walk: choose a cardinal direction (sometimes turning), a whole number
 * of tiles, and clamp the resulting tile-aligned goal inside the bounds. If the chosen
 * direction would leave the actor pinned against a wall (zero usable distance), it
 * retries the other axes so a cornered resident always finds somewhere to stroll.
 */
function chooseWalk(
  state: WanderState,
  bounds: WanderBounds,
  rng: RandomSource,
  cfg: WanderConfig,
): { dir: Cardinal; goalX: number; goalY: number } {
  const minX = bounds.x + bounds.margin;
  const maxX = bounds.x + bounds.width - bounds.margin;
  const minY = bounds.y + bounds.margin;
  const maxY = bounds.y + bounds.height - bounds.margin;

  // Candidate directions: prefer a turn (per turnChance), else keep going; but try all
  // four so a wall never traps the actor in a no-op walk.
  const wantTurn = rng.float() < cfg.turnChance;
  const order: Cardinal[] = [];
  if (!wantTurn) order.push(state.facing);
  for (const c of CARDINALS) if (!order.includes(c)) order.push(c);
  // Shuffle the turn candidates a touch so turns aren't always the same rotation.
  for (let i = order.length - 1; i > (wantTurn ? 0 : 1); i--) {
    const j = (wantTurn ? 0 : 1) + Math.floor(rng.float() * (i - (wantTurn ? 0 : 1) + 1));
    [order[i], order[j]] = [order[j]!, order[i]!];
  }

  const tiles = cfg.minTiles + Math.floor(rng.float() * (cfg.maxTiles - cfg.minTiles + 1));
  const dist = tiles * cfg.tile;

  for (const dir of order) {
    const v = cardinalVector(dir);
    const rawX = state.x + v.dx * dist;
    const rawY = state.y + v.dy * dist;
    const goalX = Math.min(Math.max(rawX, minX), maxX);
    const goalY = Math.min(Math.max(rawY, minY), maxY);
    // Usable only if the clamp didn't collapse the move to ~nothing.
    if (Math.abs(goalX - state.x) > 0.5 || Math.abs(goalY - state.y) > 0.5) {
      return { dir, goalX, goalY };
    }
  }
  // Fully boxed in (tiny bounds): stay put, keep facing.
  return { dir: state.facing, goalX: state.x, goalY: state.y };
}

/**
 * Advance one actor's deliberate wander by `dt` seconds. Mutates + returns the state's
 * position/facing/phase and reports motion transitions. Steady speed, cardinal-only
 * travel, tile-quantized distance, and a calm idle between walks — the classic
 * Pokemon-town read, never a floaty drift.
 */
export function stepWander(
  state: WanderState,
  dt: number,
  bounds: WanderBounds,
  rng: RandomSource = MATH_RANDOM,
  cfg: WanderConfig = DEFAULT_WANDER,
): WanderStep {
  const out: WanderStep = { moving: false, startedWalk: false, stopped: false, turned: false };

  if (state.phase === 'pausing') {
    state.pauseLeft -= dt;
    if (state.pauseLeft > 0) return out;
    // Begin a fresh, deliberate walk.
    const walk = chooseWalk(state, bounds, rng, cfg);
    if (walk.goalX === state.x && walk.goalY === state.y) {
      // Nowhere to go (boxed in) — idle a little longer, never thrash.
      state.pauseLeft = cfg.minPause + rng.float() * (cfg.maxPause - cfg.minPause);
      return out;
    }
    if (walk.dir !== state.facing) {
      state.facing = walk.dir;
      out.turned = true;
    }
    state.goalX = walk.goalX;
    state.goalY = walk.goalY;
    state.phase = 'walking';
    out.startedWalk = true;
    out.moving = true;
    // Fall through to advance a little this same frame.
  }

  // phase === 'walking'
  const dx = state.goalX - state.x;
  const dy = state.goalY - state.y;
  const dist = Math.hypot(dx, dy);
  if (dist < 0.5) {
    // Arrived: snap exactly onto the tile goal, then stop + idle a varied beat.
    state.x = state.goalX;
    state.y = state.goalY;
    state.phase = 'pausing';
    state.pauseLeft = cfg.minPause + rng.float() * (cfg.maxPause - cfg.minPause);
    out.stopped = true;
    out.moving = out.startedWalk; // moved this frame only if we just started
    return out;
  }
  const step = Math.min(dist, cfg.speed * dt);
  state.x += (dx / dist) * step;
  state.y += (dy / dist) * step;
  out.moving = true;
  // If this frame's step reached the goal, settle now (no dead extra frame at the
  // destination) — snap exactly on, stop, and idle a varied beat.
  if (step >= dist - 1e-6) {
    state.x = state.goalX;
    state.y = state.goalY;
    state.phase = 'pausing';
    state.pauseLeft = cfg.minPause + rng.float() * (cfg.maxPause - cfg.minPause);
    out.stopped = true;
  }
  return out;
}

// ── follower trailing (cardinal, deliberate) ────────────────────────────────────

/**
 * A follower trails its leader at a fixed gap, snapping to whole-tile lag so it walks
 * the SAME deliberate cardinal path a step behind (no floaty diagonal catch-up). The
 * scene records leader breadcrumbs; this returns where the follower should now stand
 * and which way it faces. Returns null facing when it shouldn't move (already caught up).
 */
export interface FollowResult {
  x: number;
  y: number;
  facing: Cardinal | null;
  moving: boolean;
}

/**
 * Move `follower` toward `target` at `speed` (px/sec), one cardinal axis at a time so
 * the trail reads as deliberate walking rather than a diagonal glide. Snaps onto the
 * target when within `arrive` px.
 */
export function stepFollow(
  follower: { x: number; y: number },
  target: { x: number; y: number },
  speed: number,
  dt: number,
  arrive = 1.5,
): FollowResult {
  const dx = target.x - follower.x;
  const dy = target.y - follower.y;
  const dist = Math.hypot(dx, dy);
  if (dist <= arrive) {
    return { x: target.x, y: target.y, facing: null, moving: false };
  }
  // Move along the dominant axis first (cardinal walking, never diagonal gliding).
  const horizontal = Math.abs(dx) > Math.abs(dy);
  const max = speed * dt;
  let nx = follower.x;
  let ny = follower.y;
  let facing: Cardinal;
  if (horizontal) {
    const step = Math.min(Math.abs(dx), max);
    nx += Math.sign(dx) * step;
    facing = dx >= 0 ? 'east' : 'west';
  } else {
    const step = Math.min(Math.abs(dy), max);
    ny += Math.sign(dy) * step;
    facing = dy >= 0 ? 'south' : 'north';
  }
  return { x: nx, y: ny, facing, moving: true };
}

/**
 * Walk back along a leader's breadcrumb `trail` until `gap` px of arc-length have been
 * consumed, returning the point a follower should aim for — so it trails the leader's
 * actual PATH (around scenery) rather than cutting a straight diagonal to the leader.
 * Returns the trail head if the whole trail is shorter than `gap`, or null if empty.
 */
export function trailPointBehind(
  trail: readonly { x: number; y: number }[],
  gap: number,
): { x: number; y: number } | null {
  if (trail.length === 0) return null;
  let remaining = gap;
  for (let i = trail.length - 1; i > 0; i--) {
    const a = trail[i]!;
    const b = trail[i - 1]!;
    const seg = Math.hypot(a.x - b.x, a.y - b.y);
    if (seg >= remaining) {
      const t = seg === 0 ? 0 : remaining / seg;
      return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
    }
    remaining -= seg;
  }
  return trail[0] ?? null;
}

// ── deterministic NPC lead-Pokemon pick (for NPC followers) ──────────────────────

/** The shape we need off a SimTrainer to choose its visible lead follower. */
export interface LeadSource {
  readonly id: string;
  readonly team: readonly { readonly speciesId: string }[];
  /** The trainer's own avatar species — a graceful fallback when their team is empty. */
  readonly avatarSpeciesId?: string;
}

/**
 * Deterministically choose the species id of the Pokemon that walks behind an NPC.
 * Picks from their society team (a stable hash of the trainer id selects which member,
 * so the same NPC always leads with the same partner), falling back to their avatar
 * species, then null. Pure given the trainer — re-entry / re-sim never reshuffles it.
 */
export function pickLeadSpeciesId(trainer: LeadSource): string | null {
  const team = trainer.team ?? [];
  if (team.length > 0) {
    const i = hashString(trainer.id) % team.length;
    return team[i]?.speciesId ?? team[0]!.speciesId;
  }
  return trainer.avatarSpeciesId ?? null;
}

/** A small, stable FNV-1a string hash (mirrors trainerAvatar's, kept local + pure). */
function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
